import React, { useState } from 'react';
import { Check, ChevronRight, Shield, Users, FileText } from 'lucide-react';
import { saveToGoogleSheets, updatePaymentStatus } from '../utils/googleSheets';
import TossPayment from './TossPayment';
import { sendAlimTalk } from '../utils/alimTalk';

export default function KoimaSignup() {
  const [formData, setFormData] = useState({
    companyName: '',
    ceoName: '',
    phone: '',
    ceoEmail: '',
    managerName: '',
    managerPhone: '',
    managerEmail: '',
    recommender: ''
  });

  // 대표자와 동일 체크박스 상태
  const [sameAsCEO, setSameAsCEO] = useState(false);
  
  // 필드별 에러 상태 추가
  const [fieldErrors, setFieldErrors] = useState({
    companyName: '',
    ceoName: '',
    phone: '',
    ceoEmail: '',
    managerName: '',
    managerPhone: '',
    managerEmail: ''
  });

  // URL 파라미터로 결제 결과 확인 (토스페이먼츠 공식 방식)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const orderId = urlParams.get('orderId');
    const paymentKey = urlParams.get('paymentKey');

    if (paymentStatus === 'success' && paymentKey) {
      console.log('결제 성공, 주문 ID:', orderId, '결제 키:', paymentKey);
      
      // URL에서 이메일 정보 가져오기
      const email = urlParams.get('email');
      console.log('URL에서 가져온 이메일:', email);
      
      // 결제 검증 및 완료 처리
      const processPayment = async () => {
        try {
          // 서버에서 결제 검증 (금액은 고정값 사용)
          const confirmResponse = await fetch('/api/payment/confirm', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              paymentKey: paymentKey,
              orderId: orderId,
              amount: 275000 // 고정 금액 사용 (4분기 기준: 연회비 75,000 + 입회비 200,000)
            })
          });
          
          const confirmResult = await confirmResponse.json();
          
          if (confirmResult.success) {
            console.log('결제 검증 성공:', confirmResult.data);
            
            // Google Sheets에 결제 완료 상태 업데이트
            const updateResult = await updatePaymentStatus(
              email || formData.ceoEmail, // URL에서 가져온 이메일 우선 사용
              {
                paymentKey: paymentKey,
                orderId: orderId,
                amount: 275000,
                paymentStatus: '결제완료'
              }
            );
            
            if (updateResult.success) {
              console.log('결제 완료 상태 업데이트 성공');
            } else {
              console.error('결제 완료 상태 업데이트 실패');
            }
            
            setShowComplete(true);
          } else {
            console.error('결제 검증 실패:', confirmResult.error);
            alert('결제 검증에 실패했습니다. 관리자에게 문의해주세요.');
            setShowPaymentGuide(true);
          }
        } catch (error) {
          console.error('결제 완료 처리 중 오류:', error);
          alert('결제 처리 중 오류가 발생했습니다. 관리자에게 문의해주세요.');
          setShowPaymentGuide(true);
        }
      };
      
      processPayment();
      // URL 파라미터 제거
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'fail') {
      console.log('결제 실패, 주문 ID:', orderId);
      alert('결제가 실패했습니다. 다시 시도해주세요.');
      setShowPaymentGuide(true);
      // URL 파라미터 제거
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    marketing: false
  });

  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [showTermDetail, setShowTermDetail] = useState(null);
  const [showComplete, setShowComplete] = useState(false);
  const [showPaymentGuide, setShowPaymentGuide] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 전화번호 포맷팅
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  // 이메일 유효성 검증
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 전화번호 유효성 검증
  const validatePhone = (phone) => {
    const phoneRegex = /^(\d{3}-\d{4}-\d{4}|\d{3}-\d{3}-\d{4}|\d{2}-\d{4}-\d{4}|\d{2}-\d{3}-\d{4})$/;
    return phoneRegex.test(phone);
  };

  // 필드 유효성 검증
  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'companyName':
        if (!value.trim()) error = '회사명을 입력해주세요.';
        else if (value.length < 2) error = '회사명은 2자 이상 입력해주세요.';
        break;
      
      case 'ceoName':
        if (!value.trim()) error = '대표자명을 입력해주세요.';
        else if (value.length < 2) error = '대표자명은 2자 이상 입력해주세요.';
        break;
      
      case 'phone':
        if (!value) error = '휴대폰번호를 입력해주세요.';
        else if (!validatePhone(value)) error = '올바른 휴대폰번호 형식이 아닙니다. (예: 010-1234-5678)';
        break;
      
      case 'ceoEmail':
        if (!value) error = '이메일을 입력해주세요.';
        else if (!validateEmail(value)) error = '올바른 이메일 형식이 아닙니다.';
        break;
      
      case 'managerName':
        if (!value.trim()) error = '담당자명을 입력해주세요.';
        else if (value.length < 2) error = '담당자명은 2자 이상 입력해주세요.';
        break;
      
      case 'managerPhone':
        if (!value) error = '담당자 연락처를 입력해주세요.';
        else if (!validatePhone(value)) error = '올바른 연락처 형식이 아닙니다. (예: 010-1234-5678)';
        break;
      
      case 'managerEmail':
        if (!value) error = '담당자 이메일을 입력해주세요.';
        else if (!validateEmail(value)) error = '올바른 이메일 형식이 아닙니다.';
        break;
    }
    
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 값 업데이트
    if (name === 'phone' || name === 'managerPhone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      
      // 포맷된 값으로 유효성 검증
      const error = validateField(name, formattedValue);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // 실시간 유효성 검증
      const error = validateField(name, value);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  // 대표자와 동일 체크박스 핸들러
  const handleSameAsCEO = (checked) => {
    setSameAsCEO(checked);
    
    if (checked) {
      // 체크 시 담당자 정보에 회사정보 복사
      setFormData(prev => ({
        ...prev,
        managerName: prev.ceoName,
        managerPhone: prev.phone,
        managerEmail: prev.ceoEmail
      }));
      
      // 에러 상태 초기화
      setFieldErrors(prev => ({
        ...prev,
        managerName: '',
        managerPhone: '',
        managerEmail: ''
      }));
    } else {
      // 체크 해제 시 담당자 정보 초기화
      setFormData(prev => ({
        ...prev,
        managerName: '',
        managerPhone: '',
        managerEmail: ''
      }));
      
      // 에러 상태 초기화
      setFieldErrors(prev => ({
        ...prev,
        managerName: '',
        managerPhone: '',
        managerEmail: ''
      }));
    }
  };
  
  // 포커스 아웃 시 검증
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleAgreementChange = (type) => {
    setAgreements(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const handleAllAgreements = () => {
    const allChecked = agreements.terms && agreements.privacy && agreements.marketing;
    setAgreements({
      terms: !allChecked,
      privacy: !allChecked,
      marketing: !allChecked
    });
  };

  const confirmAgreements = () => {
    if (!agreements.terms || !agreements.privacy) {
      alert('필수 약관에 모두 동의해주세요.');
      return;
    }
    setAgreedToTerms(true);
    setShowAgreementModal(false);
  };

  const getTermContent = (type) => {
    const contents = {
      terms: `제1장 총칙

제1조 (목적)
본 약관은 정관 제6조 제2항의 규정에 의하여 한국수입협회(이하 "협회")가 운영하는 온라인 플랫폼 및 관련 서비스(이하 "서비스")의 이용과 관련하여 협회와 이용자의 권리, 의무 및 책임사항, 그리고 회원에 관한 세부사항을 규정함을 목적으로 한다.

제2조 (정의)
1. "회원"이란 협회 회원규정에 따라 가입한 자로서 본 서비스를 이용하는 자를 말하며, 그 구분은 정회원, 개인회원, 일반회원, 명예회원, 사이버회원으로 나뉜다.
2. "이용자"란 회원을 포함하여 서비스에 접속하여 이용하는 자를 말한다.
3. 기타 용어는 협회 정관 및 회원규정에서 정하는 바에 따른다.

제3조 (약관의 효력 및 변경)
1. 본 약관은 서비스 화면에 게시하거나 기타의 방법으로 공지함으로써 효력을 발생한다.
2. 협회는 관련 법령을 위반하지 않는 범위에서 본 약관을 개정할 수 있다.
3. 약관을 변경하는 경우 적용일자 및 변경사유를 명시하여 사전에 공지한다.
4. 이용자가 변경된 약관에 동의하지 않는 경우 서비스 이용을 중단하고 탈퇴할 수 있으며, 계속 이용하는 경우 변경된 약관에 동의한 것으로 본다.

제4조 (약관 외 준칙)
본 약관에 명시되지 않은 사항은 협회 정관, 회원규정, 회원업무처리요령 및 관련 법령을 따른다.

제2장 이용계약 및 회원가입

제5조 (이용계약의 성립 및 가입)
1. 서비스 이용계약은 이용자가 본 약관에 동의하고 협회가 이를 승인함으로써 성립한다.
2. 정회원 또는 개인회원으로 가입하고자 하는 자는 회원업무처리요령이 정하는 바에 따라 소정의 서류를 제출하고 입회비와 연회비를 선납하여야 한다.
3. 일반회원과 명예회원 및 사이버회원으로 가입하고자 하는 자는 소정의 절차에 따라 승인을 득하여야 한다.
4. 가입은 입회서류 제출 및 회비를 완납한 후 회원업무처리요령에 의거 심사하여 가입여부를 결정한다. 단, 명예회원의 가입은 이사회에 부의하여 결정한다.

제6조 (이용신청의 제한)
1. 타인의 명의를 도용하거나 허위 정보를 기재한 경우 협회는 승인을 제한할 수 있다.
2. 법령 또는 약관을 위반할 우려가 있거나 기타 운영상 필요하다고 판단하는 경우 이용신청을 승인하지 않을 수 있다.

제7조 (자격 회복)
1. 연회비 미납으로 자격을 상실한 일반회원이 체납회비를 완납한 경우에는 정회원의 자격을 회복할 수 있다.
2. 단, 회원증강위원회에서 일반회원의 정회원 자격회복을 위한 한시적 구제방법을 정한 경우에는 이를 우선 적용한다.

제3장 회비 및 기부금

제8조 (회비)
1. 가입정회원 또는 개인회원이 부담할 입회비는 금 200,000원, 연회비는 금 300,000원으로 하며, 협회 운영비에 충당함을 원칙으로 한다.
2. 신규회원의 가입 초년도 연회비는 가입시점에 따라 다음 각 호와 같이 차등 적용한다.
  1) 1/4분기 가입자 : 300,000원
  2) 2/4분기 가입자 : 225,000원
  3) 3/4분기 가입자 : 150,000원
  4) 4/4분기 가입자 : 75,000원
3. 일반회원과 명예회원 및 사이버회원은 회비납부의무가 없다.
4. 연회비는 신규회원의 경우 가입시에 선납해야 하며 정회원의 경우 매년 2월말까지 선납해야 한다. 단, 정회원이 3월 이후부터 연말까지 연회비를 납부하지 않아 정회원자격이 상실되기 전까지는 정회원으로 간주한다.
5. 회원이 본 협회를 탈퇴 또는 제명되었을 때에는 기 납입된 입회비, 연회비는 이를 반환하지 아니한다.

제9조 (특별회비 및 기부금)
1. 회원은 협회의 발전을 위하여 특별회비를 납부할 수 있다. 특별회비의 기준은 특별회비운영요령 제3조에 따른다.
2. 본 협회의 회원은 경상경비 충당 및 협회 발전을 위하여 기부금을 납입할 수 있다. 다만, 협회는 지정기부금 발행 단체가 아니므로 기부금 공제 대상이 아니다.

제4장 서비스 이용 및 의무

제10조 (서비스의 제공)
1. 협회 사업 및 행사 관련 정보 제공
2. 회원 간 정보 교류 및 네트워크 지원
3. 협회 정책, 사업, 공지사항 제공
4. 기타 협회가 정하는 서비스

제11조 (서비스의 변경 및 중지)
1. 서비스는 연중무휴 1일 24시간 제공함을 원칙으로 하나, 시스템 점검이나 장애 시 일시 중지될 수 있다.
2. 협회는 운영상 필요에 따라 서비스를 변경할 수 있으며, 관련 내용을 사전에 공지한다.

제12조 (이용자의 신고 의무)
1. 회원은 사업체나 신상(연락처, 주소 등)에 변동이 있을 때에는 지체 없이 협회에 신고하여야 한다.
2. 회원은 거래선이나 거래분야 등 사업상의 변동이 있을 때에도 이를 협회에 신고하여야 한다.
3. 전항의 신고된 범위 내에서 공식적으로 인정될 수 있으며 권리를 주장할 수 있다.

제13조 (이용자의 금지행위)
1. 타인의 정보 도용 및 허위 정보 등록
2. 서비스 운영 방해 및 협회의 정상적인 운영을 저해하는 행위
3. 거래질서를 문란케 하여 회원 상호간 또는 비회원간에 물의를 야기하는 행위

제5장 징계 및 포상

제14조 (징계)
회원이 다음 각 호의 1에 해당한 때에는 회원관리 및 윤리위원회의 결의에 따라 회장은 필요한 징계 또는 제재조치를 취한다.
1. 관계당국으로부터 법령위배에 따른 제재조치를 당한 회원
2. 정관 제12조 제2항 각호의 1에 해당하는 자로서 그 정도가 경미한 회원
3. 거래질서 유지와 분쟁조정에 대한 윤리위원회의 조정 권고를 위배한 회원
4. 기타 세간의 물의를 야기하여 협회와 업계에 상당한 누를 끼친 회원

제15조 (포상)
본 협회의 회원이 다음 각 호에 해당할 때에는 이를 포상할 수 있다.
1. 본 협회의 발전에 현저한 공로가 있는 회원
2. 수출입시장개척 및 정책부응, 물가안정 등에 현저한 공로가 있는 회원
3. 기타 포상이 필요하다고 인정되는 회원

제6장 기타 사항

제16조 (개인정보 보호)
개인정보의 수집, 이용 및 보호에 관한 사항은 홈페이지에 별도로 게시된 '개인정보처리방침'에 따른다.

제17조 (지적재산권)
1. 서비스 및 모든 콘텐츠에 대한 권리는 협회에 귀속된다.
2. 이용자는 협회의 사전 승인 없이 이를 복제, 배포, 이용할 수 없다.

제18조 (면책 및 관할법원)
1. 협회는 이용자 간 또는 이용자와 제3자 간 거래에 대하여 책임을 지지 않는다.
2. 본 약관과 관련하여 발생한 분쟁은 협회 소재지를 관할하는 법원을 관할법원으로 한다.

부칙
1. (시행일) 본 약관은 2018년 12월 1일부터 시행한다.
2. (경과조치) 제4조(회비)와 관련된 개정 사항은 2025년 12월 17일 및 2026년 이사회의 결정에 따라 즉시 적용된다.`,
      privacy: `개인정보 수집 및 이용에 대한 동의

(사)한국수입협회(이하 '협회'라 한다)는 개인정보 보호법 제30조에 따라 정보주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리지침을 수립․공개합니다.

<적용> 협회에서 운영하는 모든 사이트에도 동일하게 적용됩니다.
단, 사이트의 서비스 특수성 및 전문성을 고려하여 상세한 개인정보 관리가 요구되는 경우에는 별도의 개인정보 처리방침을 둘 수 있습니다.

개인정보의 수집 및 이용 동의

1. 개인정보의 수집·이용목적
가. 서비스 제공에 관한 업무 이행 - 컨텐츠 제공, 특정 맞춤 서비스 제공(마이페이지, 뉴스레터 등), 기업 애로상담
나. 회원관리
-회원제 서비스 이용 및 제한적 본인 확인제에 따른 본인확인, 개인식별, 가입의사 확인, 가입 및 가입횟수 제한, 추후 법정 대리인 본인확인, 분쟁 조정을 위한 기록보존, 불만처리 등 민원처리, 공지사항 전달

2. 수집하는 개인정보의 항목

개인정보항목	사업자	필수	사업자유형, 사업자번호, 사업장명, 사업체주소, 개인/법인구분, 사업체 설립일, 전화번호, 업태(대표), 종목, 주요생산품, 직원수
선택	팩스번호, 홈페이지, 매출규모
사용자	필수	아이디, 비밀번호, 이름, 이메일주소, 휴대전화번호
선택	생년월일, 회사전화번호, 이메일 수신동의 여부

가. 홈페이지 접속 시 시스템 개선과 보완을 위하여 다음과 같은 정보가 자동으로 수집 저장됩니다.
-서비스 이용기록, 접속로그, 쿠키, 접속 IP Address, 불량이용기록

3. 개인정보의 보유 및 이용기간
원칙적으로 보유기간의 경과, 개인정보의 수집 및 이용목적의 달성 등 그 개인정보가 불필요하게 되었을 때에는 지체 없이 파기합니다. 다만, 다른 법령에 따라 보존하여야 하는 경우에는 그러하지 않을 수 있습니다. 불필요하게 되었을 때에는 지체 없이 해당 개인정보를 파기합니다.
회원정보
-탈퇴 후 지체없이 파기

4. 동의거부권 및 불이익
정보주체는 개인정보 수집에 동의를 거부할 권리가 있습니다. 다만, 필수 항목에 대한 동의를 거부할 시 저희가 제공하는 서비스를 이용할 수 없습니다.

5. 14세 미만 아동의 경우 회원가입 및 저희가 제공하는 서비스를 이용할 수 없습니다.`,
      marketing: `마케팅 이용에 대한 동의

개인정보보호법 제22조 제5항에 의해 해당 항목을 거부하시더라도 서비스를 이용하실 수 있습니다.

1. 개인정보의 수집, 이용 목적
- 한국수입협회 사업안내, 각종 뉴스레터 발송, 거래알선 및 해외마케팅 안내, 알림 서비스 제공, 맞춤형 서비스 권유 등

2. 수집하는 개인정보의 항목
- 성명(한글), 전화번호, 이메일, 휴대전화

3. 개인정보의 보유 및 이용기간 : 회원 탈퇴 시까지
※ 단, 법률이 정하는 바에 따라 탈퇴 후에도 일정기간 보유할 수 있습니다.

4. 전송방법
전자우편, 문자(SMS 또는 카카오 알림톡), 푸시, 전화, 팩스 등을 통해 제공합니다.

5. 개인정보의 마케팅 활용 동의를 거부하시더라도 회원 가입 시 제한은 없습니다. 다만, 마케팅 활용 서비스 안내 및 참여에 제한이 있을 수 있습니다.

6. 수신 동의의 변경
"마이페이지 > 회원정보수정"을 통해 직접 변경하시거나 경영기획실 (management@koima.or.kr, 02-790-6947)를 통해 수신동의를 변경(동의/철회) 할 수 있습니다.`
    };
    return contents[type] || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 이미 제출 중이면 중복 제출 방지
    if (isSubmitting) return;
    
    // 모든 필드 검증 (담당자 정보 포함)
    const requiredFields = ['companyName', 'ceoName', 'phone', 'ceoEmail', 'managerName', 'managerPhone', 'managerEmail'];
    let hasError = false;
    let newErrors = {};
    
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        hasError = true;
        newErrors[field] = error;
      }
    });
    
    if (hasError) {
      setFieldErrors(newErrors);
      alert('입력 항목을 확인해주세요.');
      return;
    }

    // 약관 동의 검증
    if (!agreedToTerms) {
      alert('약관에 동의해주세요.');
      return;
    }

    // 제출 시작
    setIsSubmitting(true);

    try {
      // Google Sheets에 데이터 저장
      const result = await saveToGoogleSheets({
        ...formData,
        termsAgreed: agreements.terms,
        privacyAgreed: agreements.privacy,
        marketingAgreed: agreements.marketing
      });

      if (result.success) {
        console.log('데이터 저장 성공');
        
        // 회비 납부 안내 페이지로 즉시 전환 (사용자 경험 개선)
        setShowPaymentGuide(true);
        
        // 백엔드 서버를 통한 알림톡 발송 (백그라운드에서 처리)
        sendAlimTalk({
          companyName: formData.companyName,
          ceoName: formData.ceoName,
          phone: formData.phone
        }).then(alimTalkResult => {
          if (alimTalkResult.success) {
            console.log('알림톡 발송 성공');
          } else {
            console.error('알림톡 발송 실패:', alimTalkResult.error);
            // 알림톡 실패해도 회원가입은 진행
          }
        }).catch(error => {
          console.error('알림톡 발송 중 오류:', error);
        });
      } else {
        alert('데이터 저장에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('회원가입 처리 중 오류:', error);
      alert('회원가입 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      // 제출 완료
      setIsSubmitting(false);
    }
  };

  // 결제 성공 처리 (토스페이먼츠 공식 방식에서는 사용되지 않음)
  // URL 리다이렉트 방식으로 처리되므로 이 함수는 호출되지 않습니다.
  const handlePaymentSuccess = async (paymentData) => {
    console.log('결제 성공 (콜백):', paymentData);
    // 이 함수는 토스페이먼츠 공식 방식에서는 호출되지 않습니다.
    // URL 리다이렉트 방식으로 처리됩니다.
  };

  // 결제 실패 처리
  const handlePaymentFail = (error) => {
    console.error('결제 실패:', error);
    alert('결제가 실패했습니다. 다시 시도해주세요.');
    setShowPayment(false);
    setShowPaymentGuide(true); // 결제 실패 시 결제 안내 페이지로 이동
  };

  // 결제 취소 처리
  const handlePaymentCancel = () => {
    setShowPayment(false);
  };

  // 결제 확인 페이지
  if (showPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="bg-white rounded-lg p-2 inline-block mb-4">
              <img 
                src="/koima_logo.png" 
                alt="KOIMA 로고" 
                className="h-12 w-auto"
                onError={(e) => {
                  console.log('로고 로딩 실패');
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div 
                style={{display: 'none'}} 
                className="h-12 w-20 bg-blue-600 rounded flex items-center justify-center text-white font-bold"
              >
                KOIMA
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="h-px w-8 bg-blue-300"></div>
              <h1 className="text-xl font-bold text-gray-900">
                결제 확인
              </h1>
              <div className="h-px w-8 bg-blue-300"></div>
            </div>
          </div>
          
          <TossPayment
            userInfo={formData}
            onFail={handlePaymentFail}
            onCancel={handlePaymentCancel}
          />
        </div>
      </div>
    );
  }

  // 회비 납부 안내 페이지
  if (showPaymentGuide) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="bg-white rounded-lg p-2 inline-block mb-4">
              <img 
                src="/koima_logo.png" 
                alt="KOIMA 로고" 
                className="h-12 w-auto"
                onError={(e) => {
                  console.log('회비 납부 안내 페이지 로고 로딩 실패');
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div 
                style={{display: 'none'}} 
                className="h-12 w-20 bg-blue-600 rounded flex items-center justify-center text-white font-bold"
              >
                KOIMA
              </div>
            </div>
            
            {/* 회원가입 완료 메시지 추가 */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center mb-2">
                <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="text-lg font-semibold text-green-800">회원가입이 진행중입니다.</h2>
              </div>
              <p className="text-sm text-green-700">회비 납부 후 정식 회원으로 활동하실 수 있습니다.</p>
            </div>
            
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="h-px w-8 bg-blue-300"></div>
              <h1 className="text-xl font-bold text-gray-900">
                회비 납부 안내
              </h1>
              <div className="h-px w-8 bg-blue-300"></div>
            </div>
          </div>
          
          <div className="space-y-6 mb-8">
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4 text-center">회비 안내</h3>
              <ul className="space-y-2 text-gray-700 text-left">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span><strong>회비:</strong> 275,000원 (연회비 75,000원, 입회비 200,000원)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span><strong>계좌번호:</strong><br/>신한은행 140-001-715194<br/>(예금주: 한국수입협회)</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3 text-center">안내사항</h3>
              <ul className="space-y-2 text-gray-700 text-sm text-left">
                <li className="flex items-start">
                  <span className="text-gray-500 mr-2">✓</span>
                  <span>담당자가 사업자등록증 및 가입신청서 제출 안내 메일을 발송해 드리오니 메일을 확인해 주시기 바랍니다.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-500 mr-2">✓</span>
                  <span>회비 납부 및 제출서류 확인 후 회원가입이 최종 완료됩니다.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-500 mr-2">✓</span>
                  <span>가입이 완료되면 회원증이 발급되며, 마이페이지에서 출력하실 수 있습니다.</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => setShowPayment(true)}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
            >
              회비 카드 결제하기
            </button>
            
            {/* <button
              onClick={() => setShowComplete(true)}
              className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
            >
              나중에 결제하기
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  // 완료 페이지 (결제 완료 후)
  if (showComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          {/* 로고 */}
          <div className="bg-white rounded-lg p-2 inline-block mb-6">
            <img 
              src="/koima_logo.png" 
              alt="KOIMA 로고" 
              className="h-12 w-auto"
              onError={(e) => {
                console.log('결제 완료 페이지 로고 로딩 실패');
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div 
              style={{display: 'none'}} 
              className="h-12 w-20 bg-blue-600 rounded flex items-center justify-center text-white font-bold"
            >
              KOIMA
            </div>
          </div>
          
          {/* 성공 아이콘 */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          
          {/* 제목 */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            결제가 완료되었습니다
          </h1>
          
          <p className="text-lg text-green-600 font-semibold mb-6">
            회원가입이 정상적으로 처리되었습니다
          </p>
          
          {/* 안내 메시지 */}
          <div className="text-left space-y-4 text-gray-700 mb-8">
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm">
                📧 <strong>회원증과 영수증은 사업자등록증 확인 후, 이메일 및 우편으로 발송될 예정입니다.</strong>
              </p>
            </div>
            
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
              <p className="text-sm">
                📞 <strong>추가서류 수신처</strong><br />
                팩스 : <strong>02-795-5461</strong><br />
                이메일 : <strong>members@koima.or.kr</strong><br />
                문의 : 회원서비스실 <strong>☎ 02-790-6947</strong>
              </p>
            </div>
          </div>
          
          {/* 확인 버튼 */}
          <div className="space-y-3">
            <a
              href="https://koima.or.kr"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
            >
              👉 확인 (대표 홈페이지로 이동)
            </a>
          </div>
        </div>
      </div>
    );
  }

  // 회원가입 폼
  return (
    <div className="min-h-screen pb-32 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 relative">
      {/* 헤더 - 디자인 강화 */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white relative overflow-hidden">
        {/* 배경 패턴 - 기하학적 디자인 */}
        <div className="absolute inset-0">
          {/* 점 패턴 */}
          <div className="absolute top-4 right-8 w-2 h-2 bg-white/30 rounded-full"></div>
          <div className="absolute top-8 right-16 w-3 h-3 bg-white/20 rounded-full"></div>
          <div className="absolute top-12 right-4 w-2 h-2 bg-white/25 rounded-full"></div>
          
          {/* 선 패턴 */}
          <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-white/10 to-transparent"></div>
          <div className="absolute top-0 left-1/3 w-px h-full bg-gradient-to-b from-transparent via-white/5 to-transparent"></div>
          
          {/* 기하학적 도형 */}
          <div className="absolute -top-8 -right-8 w-32 h-32 border border-white/10 rounded-full"></div>
          <div className="absolute -bottom-12 -left-12 w-40 h-40 border border-white/10 rounded-full"></div>
          
          {/* 플로팅 사각형 */}
          <div className="absolute top-1/3 left-8 w-12 h-12 bg-white/5 rounded-lg transform rotate-45"></div>
          <div className="absolute bottom-1/3 right-12 w-8 h-8 bg-white/10 rounded transform rotate-12"></div>
        </div>
        
        <div className="relative z-10">
          <div className="max-w-md mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-white rounded-lg p-2">
                  <img 
                    src="/koima_logo.png" 
                    alt="KOIMA 로고" 
                    className="h-8 w-auto"
                    onError={(e) => {
                      console.log('헤더 로고 로딩 실패');
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div 
                    style={{display: 'none'}} 
                    className="h-8 w-16 bg-white rounded flex items-center justify-center text-blue-900 font-bold text-sm"
                  >
                    KOIMA
                  </div>
                </div>
                <div className="h-8 w-px bg-white/30"></div>
                <div className="text-sm">
                  <div className="font-medium">한국수입협회</div>
                  <div className="text-xs opacity-80">Korea Importers Association</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold">한국수입협회 간편 회원가입</h1>
              <p className="text-blue-100">수출은 보국, 수입은 애국</p>
            </div>
          </div>
        </div>
        
        {/* 하단 웨이브 */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-6 fill-current text-gray-50" viewBox="0 0 1440 48" preserveAspectRatio="none">
            <path d="M0,48 L48,44 C96,40 192,32 288,26.7 C384,21 480,19 576,21.3 C672,24 768,32 864,34.7 C960,37 1056,35 1152,32 C1248,29 1344,27 1392,26 L1440,24 L1440,48 L0,48 Z"></path>
          </svg>
        </div>
      </div>

      {/* 프로그레스 바 */}
      <div className="bg-gray-200 h-1">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 h-1 w-1/3 transition-all duration-300"></div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="max-w-md mx-auto px-4 pt-8 pb-24">
                 {/* 페이지 타이틀 */}
         <div className="text-center mb-8">
           <p className="text-gray-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{
             __html: "KOIMA는 회원사 이익을 대변하며, <br>수출용 원자재·소재·부품·완제품 수입을 지원해<br>산업경쟁력과 소비자 후생에 기여합니다."
           }}></p>
           
           {/* 회원서비스실 연락처 */}
           <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
             <p className="text-blue-800 font-medium text-base">
               문의 : 회원서비스실, 02-790-6947
             </p>
           </div>
         </div>

        {/* 회원가입 폼 */}
        <div className="space-y-4">
          {/* 회사 정보 섹션 */}
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4 border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-3">
                <Users className="w-5 h-5 text-white" />
              </div>
              회사 정보
            </h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                회사명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 border ${fieldErrors.companyName ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all`}
                placeholder="회사명을 입력하세요"
                required
                autoComplete="organization"
              />
              {fieldErrors.companyName && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.companyName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                대표자명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="ceoName"
                value={formData.ceoName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 border ${fieldErrors.ceoName ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all`}
                placeholder="대표자명을 입력하세요"
                required
                autoComplete="name"
              />
              {fieldErrors.ceoName && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.ceoName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                휴대폰번호(대표자) <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 border ${fieldErrors.phone ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all`}
                placeholder="010-0000-0000"
                maxLength="13"
                required
                autoComplete="tel"
                inputMode="tel"
              />
              {fieldErrors.phone && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                이메일(대표자) <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="ceoEmail"
                value={formData.ceoEmail}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 border ${fieldErrors.ceoEmail ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all`}
                placeholder="example@company.com"
                required
                autoComplete="email"
                inputMode="email"
              />
              {fieldErrors.ceoEmail && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.ceoEmail}</p>
              )}
            </div>

          </div>

          {/* 담당자 정보 섹션 */}
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-3">
                  <Users className="w-5 h-5 text-white" />
                </div>
                담당자 정보
              </h2>
              
              {/* 대표자와 동일 체크박스 */}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsCEO}
                  onChange={(e) => handleSameAsCEO(e.target.checked)}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                />
                <span className="text-sm font-medium text-gray-700">대표자와 동일</span>
              </label>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                담당자명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="managerName"
                value={formData.managerName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={sameAsCEO}
                className={`w-full px-4 py-3 border ${fieldErrors.managerName ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all ${sameAsCEO ? 'bg-gray-100' : ''}`}
                placeholder="담당자명을 입력하세요"
                required
                autoComplete="name"
              />
              {fieldErrors.managerName && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.managerName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                담당자 연락처 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="managerPhone"
                value={formData.managerPhone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={sameAsCEO}
                className={`w-full px-4 py-3 border ${fieldErrors.managerPhone ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all ${sameAsCEO ? 'bg-gray-100' : ''}`}
                placeholder="010-0000-0000"
                maxLength="13"
                required
                autoComplete="tel"
                inputMode="tel"
              />
              {fieldErrors.managerPhone && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.managerPhone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                담당자 이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="managerEmail"
                value={formData.managerEmail}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={sameAsCEO}
                className={`w-full px-4 py-3 border ${fieldErrors.managerEmail ? 'border-red-300' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all ${sameAsCEO ? 'bg-gray-100' : ''}`}
                placeholder="manager@company.com"
                required
                autoComplete="email"
                inputMode="email"
              />
              {fieldErrors.managerEmail && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.managerEmail}</p>
              )}
            </div>
          </div>

          {/* 추천인 */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">추천인</label>
            <input
              type="text"
              name="recommender"
              value={formData.recommender}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
              placeholder="추천인을 입력하세요"
            />
          </div>

          {/* 약관 동의 버튼 */}
          <button
            type="button"
            onClick={() => setShowAgreementModal(true)}
            className={`w-full p-6 rounded-2xl shadow-lg border transition-all mb-8 ${
              agreedToTerms 
                ? 'bg-blue-50 border-blue-300' 
                : 'bg-white border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 ${
                  agreedToTerms 
                    ? 'bg-blue-600' 
                    : 'bg-gray-100'
                }`}>
                  {agreedToTerms ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <FileText className="w-5 h-5 text-gray-500" />
                  )}
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">
                    약관 동의 <span className="text-red-500">*</span>
                  </h3>
                  <p className="text-sm text-gray-500">
                    {agreedToTerms ? '약관에 동의하셨습니다' : '클릭하여 약관을 확인하세요'}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>
        </div>
      </div>

      {/* 하단 고정 버튼 - 모바일 키패드 대응 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40">
        <div className="max-w-md mx-auto px-4 py-4">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`w-full py-4 font-semibold rounded-xl transition-all shadow-lg ${
              isSubmitting 
                ? 'bg-gray-400 cursor-not-allowed text-white' 
                : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transform hover:scale-[1.02] active:scale-[0.98]'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                처리 중...
              </div>
            ) : (
              '회원가입 완료'
            )}
          </button>
        </div>
      </div>
      
      {/* 모바일 키패드 대응을 위한 하단 여백 */}
      <div className="h-20 md:h-0"></div>

      {/* 약관 동의 모달 */}
      {showAgreementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <h3 className="text-xl font-bold">약관 동의</h3>
              <p className="text-sm text-blue-100 mt-1">서비스 이용을 위한 약관에 동의해주세요</p>
            </div>
            
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* 전체 동의 */}
              <label className="flex items-center p-4 bg-blue-50 rounded-xl cursor-pointer hover:bg-blue-100 transition-colors">
                <input
                  type="checkbox"
                  checked={agreements.terms && agreements.privacy && agreements.marketing}
                  onChange={handleAllAgreements}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                />
                <span className="font-semibold text-blue-900">전체 약관에 동의합니다</span>
              </label>

              <div className="border-t pt-4 space-y-3">
                {/* 이용약관 */}
                <div className="border rounded-xl p-4">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreements.terms}
                      onChange={() => handleAgreementChange('terms')}
                      className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          이용약관 동의 <span className="text-red-500">*</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowTermDetail(showTermDetail === 'terms' ? null : 'terms')}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {showTermDetail === 'terms' ? '접기' : '보기'}
                        </button>
                      </div>
                      {showTermDetail === 'terms' && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap text-left">
                          {getTermContent('terms')}
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {/* 개인정보처리방침 */}
                <div className="border rounded-xl p-4">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreements.privacy}
                      onChange={() => handleAgreementChange('privacy')}
                      className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          개인정보처리방침 동의 <span className="text-red-500">*</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowTermDetail(showTermDetail === 'privacy' ? null : 'privacy')}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {showTermDetail === 'privacy' ? '접기' : '보기'}
                        </button>
                      </div>
                      {showTermDetail === 'privacy' && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap text-left">
                          {getTermContent('privacy')}
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {/* 마케팅 정보 수신 */}
                <div className="border rounded-xl p-4">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreements.marketing}
                      onChange={() => handleAgreementChange('marketing')}
                      className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          마케팅 정보 수신 동의 <span className="text-gray-500 text-sm">(선택)</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowTermDetail(showTermDetail === 'marketing' ? null : 'marketing')}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {showTermDetail === 'marketing' ? '접기' : '보기'}
                        </button>
                      </div>
                      {showTermDetail === 'marketing' && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap text-left">
                          {getTermContent('marketing')}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t flex gap-3">
              <button
                onClick={() => setShowAgreementModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={confirmAgreements}
                className="flex-1 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
              >
                동의하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}